import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Space, message } from 'antd';
import carSvg from '../img_car.svg';

const PlanoCarro = ({ value, onChange, disabled = false }) => {
  // value será un array de IDs de áreas marcadas: ['FRONT_LEFT_SIDE_WHEEL_ARCH', 'REAR_RIGHT_SIDE_WHEEL_ARCH', ...]
  const [areasMarcadas, setAreasMarcadas] = useState(value || []);
  const svgRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    // Cargar el SVG
    fetch(carSvg)
      .then(res => res.text())
      .then(text => {
        // Procesar el SVG para hacer las áreas clickeables
        let processedSvg = text;
        
        // Remover eventos inline y agregar clases para identificar áreas
        processedSvg = processedSvg.replace(/onmousemove="[^"]*"/g, '');
        processedSvg = processedSvg.replace(/onmouseout="[^"]*"/g, '');
        processedSvg = processedSvg.replace(/xlink:href="#"/g, '');
        
        // Agregar clase clickable a los grupos con ID
        processedSvg = processedSvg.replace(/<g id="([^"]+)">/g, '<g id="$1" class="car-area" style="cursor: pointer;">');
        
        setSvgContent(processedSvg);
      })
      .catch(err => {
        console.error('Error al cargar SVG:', err);
        message.error('Error al cargar el plano del carro');
      });
  }, []);

  // Sincronizar con value del padre, pero solo si el cambio viene del padre
  useEffect(() => {
    // Si el cambio es interno, no sincronizar
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    
    // Comparar arrays de forma segura
    const valueArray = Array.isArray(value) ? value : [];
    const currentArray = Array.isArray(areasMarcadas) ? areasMarcadas : [];
    
    // Comparar si son realmente diferentes
    const areEqual = valueArray.length === currentArray.length && 
                     valueArray.every((id, index) => id === currentArray[index]);
    
    if (!areEqual) {
      setAreasMarcadas(valueArray);
    }
  }, [value, areasMarcadas]);

  const handleAreaClick = useCallback((areaId, event) => {
    if (disabled) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    setAreasMarcadas(prevAreas => {
      // Verificar si el área ya está marcada
      const estaMarcada = prevAreas.includes(areaId);
      
      let nuevasAreas;
      if (estaMarcada) {
        // Si ya está marcada, desmarcarla (remover del array)
        nuevasAreas = prevAreas.filter(id => id !== areaId);
      } else {
        // Si no está marcada, marcarla (agregar al array)
        nuevasAreas = [...prevAreas, areaId];
      }
      
      // Marcar que el cambio es interno
      isInternalUpdate.current = true;
      
      // Llamar onChange con las nuevas áreas
      onChange?.(nuevasAreas);
      
      return nuevasAreas;
    });
  }, [disabled, onChange]);

  // Usar delegación de eventos en el contenedor del SVG
  useEffect(() => {
    if (!svgRef.current || !svgContent) return;

    const container = svgRef.current;
    
    const handleClick = (e) => {
      if (disabled) return;
      
      // Buscar el grupo padre con clase 'car-area'
      let target = e.target;
      while (target && target !== container) {
        if (target.classList && target.classList.contains('car-area')) {
          const areaId = target.getAttribute('id');
          if (areaId) {
            e.preventDefault();
            e.stopPropagation();
            handleAreaClick(areaId, e);
            return;
          }
        }
        // También verificar si el elemento está dentro de un grupo con clase 'car-area'
        const parentGroup = target.closest && target.closest('g.car-area');
        if (parentGroup) {
          const areaId = parentGroup.getAttribute('id');
          if (areaId) {
            e.preventDefault();
            e.stopPropagation();
            handleAreaClick(areaId, e);
            return;
          }
        }
        target = target.parentNode;
      }
    };

    container.addEventListener('click', handleClick);
    
    // Establecer cursor pointer en todos los grupos
    const groups = container.querySelectorAll('g.car-area');
    groups.forEach(group => {
      group.style.cursor = disabled ? 'default' : 'pointer';
      group.style.pointerEvents = 'auto';
      
      const paths = group.querySelectorAll('path');
      paths.forEach(path => {
        path.style.pointerEvents = 'auto';
      });
    });

    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [svgContent, handleAreaClick, disabled]);

  // Resaltar áreas marcadas
  useEffect(() => {
    if (svgRef.current && svgContent) {
      // Resetear estilos de todas las áreas
      const allGroups = svgRef.current.querySelectorAll('g.car-area');
      allGroups.forEach(group => {
        const paths = group.querySelectorAll('path');
        paths.forEach(path => {
          path.style.fill = '';
          path.style.opacity = '';
          path.style.stroke = '';
          path.style.strokeWidth = '';
        });
      });

      // Resaltar áreas marcadas
      areasMarcadas.forEach(areaId => {
        const group = svgRef.current.querySelector(`g#${areaId}`);
        if (group) {
          const paths = group.querySelectorAll('path');
          paths.forEach(path => {
            path.style.fill = '#ff4d4f';
            path.style.opacity = '0.7';
            path.style.stroke = '#ff4d4f';
            path.style.strokeWidth = '2';
            path.style.transition = 'all 0.3s';
            path.style.pointerEvents = 'auto';
          });
        }
      });
    }
  }, [areasMarcadas, svgContent]);

  return (
    <div>
      <Card
        title="Plano Interactivo del Vehículo"
        extra={
          <Space>
            <span style={{ fontSize: '12px', color: '#666' }}>
              Haz clic en las áreas del carro para marcar/desmarcar fallas
            </span>
            {areasMarcadas.length > 0 && (
              <Button
                size="small"
                danger
                onClick={() => {
                  isInternalUpdate.current = true;
                  setAreasMarcadas([]);
                  onChange?.([]);
                }}
                disabled={disabled}
              >
                Limpiar Todas
              </Button>
            )}
          </Space>
        }
      >
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          maxHeight: '600px', 
          overflow: 'auto',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          backgroundColor: '#fafafa'
        }}>
          <div
            ref={svgRef}
            style={{
              width: '100%',
              height: 'auto',
              minHeight: '400px',
            }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        </div>
      </Card>
    </div>
  );
};

export default PlanoCarro;

